import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EndpointService } from "./endpoint.service";
import { TenantLookup } from "../models/tenantLookup.model";
import { IGridService } from "../shared/grid/grid.service";
import { GridModel } from "../shared/grid/grid.model";
import { Tenant } from "../models/tenant.model";
import {
    CreateTenantRequest,
    TenantDetail,
    UpdateTenantRequest,
} from "../models/tenantDetail.model";
import { catchError } from "rxjs/operators";
import { ToastService } from "./toast.service";
import { Observable, of } from "rxjs";
import { ColumnDetails } from "../models/columnDetails.model";

@Injectable({
    providedIn: "root",
})
export class TenantService
    extends EndpointService
    implements IGridService<Tenant>
{
    constructor(
        http: HttpClient,
        private toastService: ToastService,
    ) {
        super(http);
    }

    get(
        filter: string,
        orderBy: string,
        sort: string,
        page: number,
        pageSize: number,
    ) {
        return this.http.get<GridModel<Tenant>>(
            this.getApiUrl("Tenant/GetTenants"),
            {
                params: this.createParams(
                    filter,
                    orderBy,
                    sort,
                    page,
                    pageSize,
                ),
            },
        );
    }

    getFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
        return this.http
            .get<{
                [property: string]: ColumnDetails;
            }>(this.getApiUrl("Tenant/GetFieldConfiguration"))
            .pipe(
                catchError((error) => {
                    console.error("Error loading field configuration:", error);
                    return of({});
                }),
            );
    }

    getTenant(id: string): Observable<TenantDetail> {
        return this.http.get<TenantDetail>(this.getApiUrl(`tenants/${id}`));
    }

    createTenant(payload: CreateTenantRequest): Observable<Tenant> {
        return this.http.post<Tenant>(
            this.getApiUrl("tenants/CreateTenant"),
            payload,
        );
    }

    updateTenant(id: string, payload: UpdateTenantRequest): Observable<Object> {
        return this.http.put(this.getApiUrl(`tenants/${id}`), payload);
    }

    changeStatus(id: string, status: boolean) {
        return this.http.put(this.getApiUrl("Tenant/ChangeStatus"), {
            id: id,
            status: status,
        });
    }

    deleteTenant(id: string) {
        return this.http.delete(this.getApiUrl(`Tenant/Delete?id=${id}`));
    }

    getTenantLookupList() {
        return this.http
            .get<TenantLookup[]>(this.getApiUrl("Tenant/GetTenantLookupList"))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading tenants",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getTenantLogoUrl(id: string) {
        return this.http.get<string>(
            this.getApiUrl(`Tenant/GetTenantLogoUrl?id=${id}`),
        );
    }

    uploadLogoImage(id: string, logo: File) {
        const formData = new FormData();
        formData.append("logo", logo);
        return this.http.post(
            this.getApiUrl(`Tenant/UploadLogoImage?id=${id}`),
            formData,
        );
    }
}
